/**
 * SessionGate — the React shell over the session states.
 *
 * The board's whole job is to tell an operator what this host is doing. That
 * makes a silent auth failure the worst possible failure mode: empty lanes and a
 * calm pill read exactly like a healthy idle host, so an operator would conclude
 * "nothing is running" when the truth is "you are not signed in". The gate exists
 * to make that impossible — when identity is the problem, identity is what the
 * page says, full-bleed, instead of the board.
 *
 * Three outcomes, and the distinction between the first two is the point:
 *
 *   - `not-permitted` (403) — terminal. The sign-in already succeeded; this
 *     account's domain is not on the allowlist. Redirecting would re-authenticate
 *     the same rejected identity and return the same 403 forever, so this screen
 *     never redirects. It names the two permitted domains and offers sign-out, the
 *     only action that can actually change the outcome.
 *   - `signed-out` (401 / a sign-in bounce) — recoverable, and handled by WHEN it
 *     happened. On first load there is nothing on screen to lose, so the gate
 *     goes straight to the proxy. Once the board has rendered, an auto-redirect
 *     would yank a working page out from under someone mid-read, so expiry gets a
 *     visible state with a one-click sign-in.
 *   - No failure — render the board, untouched.
 *
 * The redirect is guarded by {@link decideSignIn}: if a bounce was already tried
 * inside the retry window and we are still here and still signed out, the session
 * is not establishing (a cookie dropped over plain HTTP is the documented cause),
 * and re-firing would spin. Suppression turns that spin into the same visible,
 * clickable state — a loop is never preferable to a message.
 */

import { useEffect, useState } from 'react'
import { ALLOWED_EMAIL_DOMAINS, SIGN_IN_ATTEMPT_KEY, type SessionFailureKind, decideSignIn, signInUrl } from './session'

export interface SessionGateProps {
  /** The merged identity failure across every feed, or null when identity is fine. */
  failure: SessionFailureKind | null
  /**
   * True once any feed has returned data in this page's lifetime. It is what
   * separates "arrived signed out" from "session died while I was reading", and
   * therefore whether an automatic redirect is a help or a theft.
   */
  everLoaded: boolean
  /** The board. Rendered only when identity is not the problem. */
  children: React.ReactNode
}

/** Read the last sign-in attempt timestamp; null when unavailable or unparseable. */
function readLastAttempt(): number | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(SIGN_IN_ATTEMPT_KEY)
    if (raw === null || raw === undefined) {
      return null
    }
    const parsed = Number.parseInt(raw, 10)
    return Number.isFinite(parsed) ? parsed : null
  } catch {
    // A blocked/absent sessionStorage must not break sign-in. The cost is losing
    // the loop-breaker, so the manual state below is still reachable by hand.
    return null
  }
}

/** Record a sign-in attempt so the next one inside the window is suppressed. */
function recordAttempt(at: number): void {
  try {
    globalThis.sessionStorage?.setItem(SIGN_IN_ATTEMPT_KEY, String(at))
  } catch {
    // Best-effort; see readLastAttempt.
  }
}

/** Send the browser to the proxy sign-in, returning here afterwards. */
function goToSignIn(): void {
  const href = globalThis.location?.href ?? '/'
  recordAttempt(Date.now())
  if (globalThis.location) {
    globalThis.location.assign(signInUrl(href))
  }
}

export function SessionGate({ failure, everLoaded, children }: SessionGateProps): React.JSX.Element {
  // Set when an automatic bounce is suppressed by the retry window, so the
  // screen can say WHY it is asking rather than looking like a dead end.
  const [redirectSuppressed, setRedirectSuppressed] = useState(false)

  useEffect(() => {
    // Suppression describes one signed-out episode, not the tab. A session can
    // recover without this tab navigating (signing in from a second tab shares
    // the cookie; this flag does not), and carrying the note into a later expiry
    // would tell the operator a sign-in was "just attempted" when none was.
    if (failure === null) {
      setRedirectSuppressed(false)
      return
    }
    // Only the first-load signed-out case redirects on its own. A 403 must never
    // redirect (it loops), and a mid-session expiry must not steal the page.
    if (failure !== 'signed-out' || everLoaded) {
      return
    }
    if (decideSignIn(Date.now(), readLastAttempt()) === 'redirect') {
      goToSignIn()
      return
    }
    setRedirectSuppressed(true)
  }, [failure, everLoaded])

  if (failure === 'not-permitted') {
    return <NotPermittedScreen />
  }
  if (failure === 'signed-out') {
    return <SignedOutScreen everLoaded={everLoaded} redirectSuppressed={redirectSuppressed} />
  }
  return <>{children}</>
}

/**
 * The 403 screen. Terminal by design — the only affordance is signing out, since
 * the account itself is the problem and no retry with this session can change it.
 */
function NotPermittedScreen(): React.JSX.Element {
  return (
    <div className="session-screen" role="alert" data-testid="session-not-permitted">
      <h1 className="session-title">Your account is not permitted</h1>
      <p className="session-detail">
        You are signed in, but this evaluator only admits accounts on{' '}
        <strong>{ALLOWED_EMAIL_DOMAINS.join(' or ')}</strong>.
      </p>
      <p className="session-detail session-detail-quiet">
        Sign out and sign back in with a permitted account. Signing in again with this one returns you here.
      </p>
      <a className="session-action" href="/oauth2/sign_out" data-testid="session-sign-out">
        Sign out
      </a>
    </div>
  )
}

/**
 * The 401 screen. `everLoaded` picks the honest wording: an expired session is a
 * different story from arriving unauthenticated, and conflating them leaves an
 * operator unsure whether they ever saw real data.
 */
function SignedOutScreen({
  everLoaded,
  redirectSuppressed,
}: {
  everLoaded: boolean
  redirectSuppressed: boolean
}): React.JSX.Element {
  return (
    <div className="session-screen" role="alert" data-testid="session-signed-out">
      <h1 className="session-title">{everLoaded ? 'Your session expired' : 'Sign in to continue'}</h1>
      <p className="session-detail">
        {everLoaded
          ? 'The board stopped updating because this session is no longer valid. What you were looking at is no longer live.'
          : 'The evaluator read plane requires a signed-in session.'}
      </p>
      {redirectSuppressed ? (
        <p className="session-detail session-detail-quiet" data-testid="session-retry-note">
          A sign-in was just attempted and did not take hold. Try once more — if it keeps failing, the session cookie is
          not being stored.
        </p>
      ) : null}
      <button className="session-action" type="button" onClick={goToSignIn} data-testid="session-sign-in">
        Sign in
      </button>
    </div>
  )
}
